import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';
import { useApi } from '../../api/useApi';
import { productAPI } from '../../api/api';
import useIsMobile from '../../hooks/useIsMobile';
import { mapProductToCheckoutItem } from '../../utils/commerce';
import FarmLifecycleLoader from '../common/FarmLifecycleLoader';
import { filterAndSortProducts } from '../../utils/productCatalog';
import { useAuthStore } from '../../store/authStore';

const formatFarmerLocation = (farmer) => {
  if (!farmer) return '';

  const district = typeof farmer.district === 'string' ? farmer.district.trim() : '';
  const state = typeof farmer.state === 'string' ? farmer.state.trim() : '';
  if (district && state) return `${district}, ${state}`;
  if (district) return district;
  if (state) return state;

  if (typeof farmer.location === 'string') return farmer.location;

  if (farmer.location && typeof farmer.location === 'object') {
    const coords = Array.isArray(farmer.location.coordinates) ? farmer.location.coordinates : [];
    if (coords.length === 2) {
      const [lng, lat] = coords;
      return `(${lat}, ${lng})`;
    }
  }

  return '';
};

export default function ProductGrid() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const addItem = useCartStore((s) => s.addItem);
  const userProfile = useAuthStore((s) => s.userProfile);
  const [added, setAdded] = useState({});
  const { data: products, loading, error } = useApi(productAPI.getAll);

  const requireLogin = () => {
    if (userProfile) return true;
    navigate('/login', {
      state: {
        redirectTo: `${location.pathname}${location.search || ''}`,
        redirectState: location.state || null,
      },
    });
    return false;
  };

  const handleAdd = (product) => {
    if (!requireLogin()) return;
    addItem(mapProductToCheckoutItem(product), 1);
    setAdded((prev) => ({ ...prev, [product._id]: true }));
    setTimeout(() => setAdded((prev) => ({ ...prev, [product._id]: false })), 1500);
  };

  const handleBuyNow = (product) => {
    navigate('/checkout', {
      state: {
        buyNow: true,
        source: 'buy_now',
        checkoutItems: [mapProductToCheckoutItem(product)],
      },
    });
  };

  if (loading)
    return (
      <section style={{ padding: '80px 24px', textAlign: 'center' }}>
        <FarmLifecycleLoader title="Loading fresh products" subtitle="Sorting farm produce and current inventory for the store..." />
      </section>
    );

  if (error)
    return (
      <section style={{ padding: '80px 24px', textAlign: 'center' }}>
        <p style={{ color: '#ef4444' }}>Failed to load products: {error}</p>
      </section>
    );

  const productList = products || [];
  const featuredProducts = filterAndSortProducts(productList, {
    selectedCategory: 'All',
    sortBy: 'featured',
  }).slice(0, 3);
  const remainingCount = Math.max(productList.length - featuredProducts.length, 0);

  return (
    <section id="products" style={{ padding: isMobile ? '56px 16px' : '80px 24px', backgroundColor: '#f8fdf9' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '56px' }}>
          <span
            style={{
              background: '#d8f3dc',
              color: '#2d6a4f',
              padding: '6px 20px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            Fresh Produce
          </span>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '700', color: '#1a1a2e', marginTop: '16px' }}>
            Direct From the Field & Farmer
          </h2>
          <p style={{ color: '#6b7280', marginTop: '12px', fontSize: '1.1rem' }}>
            Every product sourced directly from verified farmers
          </p>
        </div>

        {productList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
            No products available right now. Check back soon!
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 220 : 260}px, 1fr))`,
              gap: isMobile ? '14px' : '28px',
            }}
          >
            {featuredProducts.map((product) => {
              const farmerLocation = formatFarmerLocation(product.farmerId);
              return (
                <div
                  key={product._id}
                  onClick={() => navigate(`/products/${product._id}`)}
                  style={{
                    background: 'white',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.07)';
                  }}
                >
                  <div style={{ position: 'relative', height: '200px', overflow: 'hidden' }}>
                    <img
                      src={product.images?.[0] || 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400'}
                      alt={product.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        background: '#2d6a4f',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: '600',
                      }}
                    >
                      {product.qualityGrade || 'Farm Fresh'}
                    </div>
                  </div>

                  <div style={{ padding: '20px' }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' }}>
                      {product.name}
                    </h3>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                      Farmer: {product.farmerId?.name || 'Local Farmer'}
                      {farmerLocation ? ` - ${farmerLocation}` : ''}
                    </p>
                    <p style={{ fontSize: '13px', color: '#52b788', marginBottom: '16px' }}>
                      Stock: {product.availableQuantityKg} kg available
                    </p>

                    <div style={{ display: 'grid', gap: '12px' }}>
                      <span style={{ fontSize: '1.4rem', fontWeight: '700', color: '#2d6a4f' }}>
                        Rs.{product.pricePerKg}
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>/kg</span>
                      </span>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAdd(product);
                          }}
                          style={{
                            background: added[product._id] ? '#52b788' : '#2d6a4f',
                            color: 'white',
                            border: 'none',
                            padding: '10px 14px',
                            borderRadius: '999px',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'background 0.3s',
                          }}
                        >
                          {added[product._id] ? 'Added' : 'Add to Cart'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBuyNow(product);
                          }}
                          style={{
                            background: 'white',
                            color: '#2d6a4f',
                            border: '1px solid #2d6a4f',
                            padding: '10px 14px',
                            borderRadius: '999px',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                          }}
                        >
                          Buy Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {remainingCount > 0 && (
              <button
                onClick={() => navigate('/products')}
                style={{
                  minHeight: isMobile ? '320px' : '360px',
                  borderRadius: '20px',
                  border: '1px dashed #52b788',
                  background:
                    'linear-gradient(135deg, rgba(216,243,220,0.96), rgba(255,255,255,0.96))',
                  boxShadow: '0 12px 32px rgba(45,106,79,0.08)',
                  padding: isMobile ? '24px 18px' : '28px 22px',
                  display: 'grid',
                  alignContent: 'center',
                  justifyItems: 'center',
                  gap: '12px',
                  color: '#1b4332',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 18px 44px rgba(45,106,79,0.14)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(45,106,79,0.08)';
                }}
              >
                <div
                  style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: '#2d6a4f',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.6rem',
                    fontWeight: '800',
                  }}
                >
                  +{remainingCount}
                </div>
                <h3 style={{ fontSize: '1.35rem', fontWeight: '800', margin: 0 }}>More Products</h3>
                <p style={{ margin: 0, textAlign: 'center', color: '#52796f', lineHeight: 1.6 }}>
                  Explore the remaining {remainingCount} products in the full products page.
                </p>
                <span
                  style={{
                    marginTop: '6px',
                    background: '#2d6a4f',
                    color: '#ffffff',
                    padding: '10px 18px',
                    borderRadius: '999px',
                    fontWeight: '700',
                    fontSize: '14px',
                  }}
                >
                  View All Products
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
